/**
 * Mira: the documents the nightly Mira job publishes at `clients/{clientId}/mira/{section}`.
 *
 * Office's read-only mirror of IRES Cloud's `SectionDocument`, `PublishedPeriod` and
 * `PublishedMetric` (`functions/src/services/mira/precomputeRun.ts`). The browser never writes
 * these: the Firestore rules refuse it, and a figure has no meaning outside the job that computed it.
 *
 * **A figure the client may not see is not in the document at all.** Only an `AVAILABLE` metric
 * carries `value`, `basis` and `breakdown`; every other state carries only the state, so a screen
 * must never read a number off a metric without checking its state first.
 */

/** Why a metric is or is not shown. Mirrors Cloud's `AvailabilityState`. */
export enum MiraAvailabilityState {
  /** Coverage at or above the metric's floor: the figure is published. */
  AVAILABLE = "AVAILABLE",
  /** Structurally impossible for this client. Rendered as nothing and never counted as a gap. */
  NOT_APPLICABLE = "NOT_APPLICABLE",
  /** Applies, but too little is captured (or too few cases). Listed as an unlock on Datenqualität. */
  INSUFFICIENT_DATA = "INSUFFICIENT_DATA",
  /** Withheld for privacy or by an open decision. Stated, never a blank. */
  SUPPRESSED = "SUPPRESSED",
  /** Our pipeline has not measured the input yet. A gap of ours, never shown as the client's. */
  UNMEASURED = "UNMEASURED",
}

/** The section documents, one per Mira screen, in the order Mira lists them. */
export enum MiraSection {
  BUSINESS_DEVELOPMENT = "businessDevelopment",
  PROCESS_FLOW = "processFlow",
  HYDRY = "hydry",
  DEVICES = "devices",
  INSURANCE = "insurance",
  DATA_QUALITY = "dataQuality",
}

export const MIRA_SECTIONS: MiraSection[] = [
  MiraSection.BUSINESS_DEVELOPMENT,
  MiraSection.PROCESS_FLOW,
  MiraSection.HYDRY,
  MiraSection.DEVICES,
  MiraSection.INSURANCE,
  MiraSection.DATA_QUALITY,
];

/** The rolling periods every section document carries. Calendar years are keyed `year-<yyyy>`. */
export const MIRA_PERIOD_ROLLING_12 = "rolling12";
export const MIRA_PERIOD_ROLLING_12_PREVIOUS = "rolling12-previous";
/**
 * The rolling year one month back: what "Letzte 12 Monate" compares to since #2610137 (its `comparesTo`),
 * so a change reads as the progression since last month. Only a comparison, never offered in the picker.
 */
export const MIRA_PERIOD_ROLLING_12_MONTH_EARLIER = "rolling12-month-earlier";
export const MIRA_PERIOD_YEAR_TO_DATE = "ytd";
/** The month (`2026-10`) and quarter (`2026-Q3`) periods the calendar offers since 2026-11-02. */
export const MIRA_PERIOD_MONTH_PATTERN = /^\d{4}-\d{2}$/;
export const MIRA_PERIOD_QUARTER_PATTERN = /^\d{4}-Q[1-4]$/;
export const MIRA_PERIOD_YEAR_PREFIX = "year-";

/** The scope of an IRES account's "Alle Mandanten" document: every published Mandant added up (#2610111). */
export const MIRA_ALL_CLIENTS_SCOPE = "ALL";

/** A qualification on a figure, as a key under `mira.caveat.*` rather than as text. */
export interface MiraCaveat {
  key: string;
  params?: (string | number)[];
}

/**
 * What a hidden figure still needs before Mira can show it, published by IRES Cloud on an
 * `INSUFFICIENT_DATA` metric and read by the "Mehr Kennzahlen freischalten" page.
 *
 * - `SHARE` is a capture rate: `needed` is the share of cases (0 to 1) that must carry the field,
 *   e.g. 0.4 means 40 %. The figure's own `coverage` is how far it has come.
 * - `COUNT` is a number of cases that must carry a thing the company records: `have` of `needed` so
 *   far, e.g. 1 of 10 restoration projects (a subpopulation figure).
 * - `CASES` is simply too few projects overall for the figure to rest on: `have` of `needed` cases in
 *   the period. Nothing to record, so the page groups these under one generic instruction and never
 *   shows the figure's own "record field X" text.
 *
 * **Optional.** Documents written before the field was added lack it, so the page must still list
 * such a figure, only without a progress bar.
 */
export type MiraUnlockTarget =
  | { kind: "SHARE"; needed: number }
  | { kind: "COUNT"; have: number; needed: number }
  | { kind: "CASES"; have: number; needed: number };

/** One metric as published for one period. */
export interface MiraPublishedMetric {
  state: MiraAvailabilityState;
  /** Only on `AVAILABLE`. `null` is a real answer: the metric applies and the period had nothing. */
  value?: number | null;
  basis?: number;
  median?: number | null;
  /**
   * The 15th and 85th percentiles of the same population as {@link median}, for the same period.
   * Published beside the median by IRES Cloud. Both optional: Office works with documents that lack
   * them, and shows a spread only when both are present. Gated by `VIEW_MIRA_PERCENTILES`.
   */
  p15?: number | null;
  p85?: number | null;
  breakdown?: Record<string, number>;
  /**
   * The display name of each key in {@link breakdown}, attached by IRES Cloud at publish time from its
   * own authoritative list (Office never keeps a copy). Used by the Versicherer card (MIRA-INS-001),
   * where the breakdown is keyed by MDIS's four-letter insurer key and this gives each the insurer's
   * short name. A breakdown key with no entry here has no name, so the view folds it into "Weitere
   * Versicherer". Optional: absent on every metric that does not name its breakdown, and on documents
   * written before the field existed.
   */
  labels?: Record<string, string>;
  caveat?: MiraCaveat;
  coverage?: number;
  /**
   * On a GROUP document only: how many of the group's companies the figure is built from. `total` is
   * the members in the group for the period, `contributing` the ones that had something to count.
   * Drives the "über {contributing} von {total} Betrieben" caption suffix. Absent on a CLIENT document.
   */
  members?: { total: number; contributing: number };
  /** On an `INSUFFICIENT_DATA` metric only, and optional even then: what still unlocks the figure. */
  unlockTarget?: MiraUnlockTarget;
  presentation?: string;
  /** Cloud's German description of the missing input. Office renders its own i18n instead. */
  unlockField?: string | null;
  /** Cloud's English diagnostic. Never shown to a user. */
  reason?: string;
}

export interface MiraPublishedPeriod {
  /** First day, `YYYY-MM-DD`, inclusive. */
  from: string;
  /** Day after the last one, `YYYY-MM-DD`. A period never includes the month the job ran in. */
  toExclusive: string;
  /** What changes compare with, for an IRES Office build before the baselines; read through `baselineKey`. */
  comparesTo?: string;
  /**
   * On a period the calendar offers (2026-11-02): the keys of its "Vorherige Periode" and "Letzte 12 Monate"
   * (`MiraBaseline.ts`). Absent on a baseline-only period, and on every period of an older document.
   */
  baselines?: { previous?: string; previous12?: string };
  population: number;
  /**
   * On a GROUP document only: part of the period lies before the group's membership was first recorded
   * (`membershipSeededAt`), so the membership is taken from the first snapshot and earlier changes are
   * not reflected. Drives a caveat line under the header. Absent on a CLIENT document.
   */
  membershipAssumed?: boolean;
  metrics: Record<string, MiraPublishedMetric>;
}

/** The three grains a {@link MiraSectionDocument.trends} entry carries. */
export type MiraTrendGrain = "month" | "quarter" | "year";

/**
 * One point on a metric's timeline, for one grain. `value` is in the same terms as the period
 * metric's `value` (a share stays a share). `null` is a real gap: the grain had nothing to show.
 */
export interface MiraTrendPoint {
  /** `"2026-09"` (month), `"2026-Q3"` (quarter) or `"2025"` (year). */
  key: string;
  /** First day of the point, `YYYY-MM-DD`, inclusive. */
  from: string;
  /** Day after the last one, `YYYY-MM-DD`. */
  toExclusive: string;
  value: number | null;
  basis: number;
  median?: number | null;
  p15?: number | null;
  p85?: number | null;
  /**
   * The parts the point's value stacks into, keyed by a stage / service id, present only for the two
   * stackable segment figures (MIRA-PHA-011 Projektablauf, MIRA-PRJ-017 Leistungen). The values sum to
   * {@link value}; a part a point has nothing for is absent or `null`. Absent for every other figure and
   * for documents written before the field was added (the stacked card falls back to the bare total then).
   */
  segments?: Record<string, number | null>;
  /** The current year to date, drawn hollow: not a full period. */
  partial?: true;
  /** Too few rows for this figure in this point; drawn as a gap with a note. */
  belowFloor?: true;
}

/** A metric's timeline at each grain, chronological ascending. */
export interface MiraTrendSeries {
  /** 24 months. */
  month: MiraTrendPoint[];
  /** 8 complete quarters. */
  quarter: MiraTrendPoint[];
  /** 3 calendar years, then the current year to date (`partial`). */
  year: MiraTrendPoint[];
}

/** The document at `clients/{clientId}/mira/{section}`. */
export interface MiraSectionDocument {
  section: string;
  /** `MARKET`: a Markt document (`miraMarket/{entity}_{section}`, #2610072), every published Mandant. */
  /** `ALL`: an IRES account's "Alle Mandanten", every published Mandant added up (#2610111). */
  /** `GROUP_VIEW`: a group member's view of its group, `groupView_{section}` (#2610112 X6d), no member named. */
  scope: "CLIENT" | "GROUP" | "MARKET" | "ALL" | "GROUP_VIEW";
  /** `MARKET` only: which Markt entity. */
  entity?: "projects" | "clients";
  clientId: string;
  /**
   * The section's totals, which a "Letzte 12 Monate" change scales to the chosen period's length (2026-11-02,
   * `MiraBaseline.ts`). Absent on an older document: `MIRA_COUNT_METRICS` stands in.
   */
  countMetrics?: string[];
  /**
   * On a GROUP document only: the member client ids valid when the job ran, and the ISO date the group
   * membership was first recorded (so a period that starts before it is marked `membershipAssumed`).
   * Both absent on a CLIENT document.
   */
  groupMembers?: string[];
  membershipSeededAt?: string;
  /**
   * On a company's GROUP document in its viewing client only (#2610112 X6e): the company and its Mandanten
   * and their branches by name, which a viewing client cannot read from the Mandanten themselves.
   */
  companyView?: MiraCompanyView;
  metricVersion: number;
  /** ISO timestamp of the precompute run that wrote it. */
  generatedAt: string;
  categories: string[];
  periods: Record<string, MiraPublishedPeriod>;
  /** The legacy per-month trend. Kept as it is; the Verlauf card reads {@link trends} instead. */
  trend: Record<string, Array<{ month: string; value: number | null }>>;
  /**
   * The timelines the Verlauf card reads: one entry per metric published in `rolling12`, at each
   * grain. Optional, so Office works with documents that predate it (the card hides itself then).
   * Snapshot metrics have no entry.
   */
  trends?: Record<string, MiraTrendSeries>;
  sectionState: MiraAvailabilityState;
  unlocks: string[];
}

/**
 * What IRES Cloud writes beside a company's figures in its viewing client (#2610112 X6e, Cloud
 * `companyView.ts`): the company, its Mandanten by name and their branches as "Standort (Mandant)".
 */
export interface MiraCompanyView {
  companyId: string;
  companyName: string;
  members: Record<string, string>;
  branchNames: Record<string, string>;
}

/**
 * The reserved branch keys in a {@link MiraBranchDocument}, outside the real branch id space.
 *
 * - `__none`: the projects with no branch.
 * - `__other`: the branches beyond the 50-branch cap, folded into one bucket.
 */
/**
 * The breakdown key on a duration figure carrying how many projects IRES Cloud left out because their event
 * was recorded late (Mira-Ausnahmen, Hans R8, #2610040): Ø Projektdauer, Freigabedauer and three Prozess
 * transitions. Absent when none were.
 */
export const MIRA_EXCLUDED_KEY = "excluded";

export const MIRA_BRANCH_NONE = "__none";
export const MIRA_BRANCH_OTHER = "__other";

/** One period of a {@link MiraBranchDocument}: the same bounds as the client's own section period. */
export interface MiraBranchPeriod {
  /** First day, `YYYY-MM-DD`, inclusive. */
  from: string;
  /** Day after the last one, `YYYY-MM-DD`. */
  toExclusive: string;
  /**
   * The figures of each branch that carries a value, keyed by branch id (plus {@link MIRA_BRANCH_NONE}
   * and {@link MIRA_BRANCH_OTHER}). A branch carries only the metrics IRES Cloud published for it, and
   * each is a {@link MiraPublishedMetric} with no breakdown and no trend. A branch with no projects in
   * the period is simply absent here; it is not "without value".
   */
  branches: Record<string, Record<string, MiraPublishedMetric>>;
}

/**
 * The branch comparison document at `clients/{clientId}/mira/branches_{section}`, read-only in Office
 * like {@link MiraSectionDocument}. IRES Cloud writes it only where at least two named branches carry a
 * value for the section, so it is absent for a single-branch client, and never written for Datenqualität.
 * It never carries a branch name (Office resolves those from the client's own branch list) and no trends.
 */
export interface MiraBranchDocument {
  section: string;
  /** `CLIENT_INSURERS`: the per-insurer document (`insurers_{section}`, #2610137), the same shape. */
  scope: "CLIENT_BRANCHES" | "CLIENT_INSURERS";
  /** Per-insurer documents only: each insurer key's short name, from IRES Cloud's managed list. */
  labels?: Record<string, string>;
  clientId: string;
  metricVersion: number;
  /** ISO timestamp of the precompute run that wrote it. */
  generatedAt: string;
  /** The same period keys as the client's own `{section}` document. */
  periods: Record<string, MiraBranchPeriod>;
  /**
   * The per-branch timelines for the comparison bars, keyed by branch id (plus {@link MIRA_BRANCH_NONE}
   * and {@link MIRA_BRANCH_OTHER}) then by metric id. Written by IRES Cloud for the totals (`month`,
   * `quarter`, `year`: MIRA-PRJ-001, MIRA-DEV-001, MIRA-DEV-002, MIRA-DEV-004) and, since 2026-10-05, for
   * the branch-comparable averages / shares as well, but for those with only `quarter` and `year` arrays
   * (an average comparison is Quartal / Jahr only; Office never reads `month` for it). A branch carries a
   * series only for the figures it is comparable on. Optional, so an older branch document (comparison card
   * only, no bars) still works, and so does one a figure's averages have not been deployed to yet.
   */
  trends?: Record<string, Record<string, MiraTrendSeries>>;
}
